import { makeStyles } from '@material-ui/core/styles'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_RADIUS,
  OUTLINED_INPUT_FOCUS_BORDER,
  getOutlinedSelectMenuPaperStyles,
} from 'lib/form-field-styles'
import {
  SEGMENTED_BUTTON_PADDING_X,
  SEGMENTED_BUTTON_PADDING_Y,
} from 'lib/segmented-button-styles'
import { SCHEDULE_AVAILABILITY_OUTSIDE_HOURS } from './schedule-availability-styles'

// Tighter toolbar control sizing when main content is ~800px or less (e.g. iPad
// or small laptop with the 250px sidebar open — viewport around 1050px).
const COMPACT_TOOLBAR_MAX_WIDTH = 1049

const toolbarControlText = {
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.5,
  color: CALLOUT_FOREGROUND,
}

const useCalendarStyles = makeStyles(theme => {
  const compactToolbar = `@media (max-width:${COMPACT_TOOLBAR_MAX_WIDTH}px)`

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    controlBar: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      width: '100%',
      boxSizing: 'border-box',
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    controlBarControls: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      flex: '0 1 auto',
      minWidth: 0,
      flexWrap: 'wrap',
    },
    mobileSegmentControl: {
      width: 'auto',
      display: 'inline-flex',
    },
    mobileSegmentButton: {
      minWidth: 72,
      flex: '0 0 auto !important',
    },
    toolbarFilters: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      flex: '0 1 auto',
      flexShrink: 0,
      marginLeft: 'auto',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      [compactToolbar]: {
        gap: theme.spacing(1),
      },
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(1),
      },
    },
    segmentControlWrapper: {
      flex: '0 0 auto',
      flexShrink: 0,
    },
    segmentControl: {
      width: 'auto !important',
      maxWidth: 'none !important',
      flex: '0 0 auto',
      flexShrink: 0,
      alignItems: 'stretch',
      borderRadius: 24,
      border: `1px solid ${OUTLINED_INPUT_BORDER} !important`,
      backgroundColor: theme.palette.common.white,
      display: 'inline-flex',
      overflow: 'hidden',
      boxSizing: 'border-box',
      '& .MuiToggleButtonGroup-groupedHorizontal': {
        borderRadius: '0 !important',
      },
      '& .MuiToggleButton-root': {
        borderTop: 'none !important',
        borderRight: 'none !important',
        borderBottom: 'none !important',
      },
    },
    segmentButton: {
      width: 'auto !important',
      minWidth: 80,
      maxWidth: 'none',
      flex: '0 0 auto !important',
      flexShrink: '0 !important',
      margin: '0 !important',
      border: 'none !important',
      borderRadius: '0 !important',
      boxShadow: 'none !important',
      color: `${CALLOUT_FOREGROUND} !important`,
      backgroundColor: `${theme.palette.common.white} !important`,
      textTransform: 'none',
      padding: `${SEGMENTED_BUTTON_PADDING_Y}px ${SEGMENTED_BUTTON_PADDING_X}px !important`,
      whiteSpace: 'nowrap',
      [compactToolbar]: {
        minWidth: 72,
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: 72,
      },
      '& .MuiToggleButton-label': {
        width: 'auto',
        display: 'flex',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
      },
      '&:not(:first-of-type)': {
        borderLeft: `1px solid ${OUTLINED_INPUT_BORDER} !important`,
      },
      '&.Mui-selected': {
        backgroundColor: `${CALLOUT_BACKGROUND} !important`,
        color: `${CALLOUT_FOREGROUND} !important`,
        border: 'none !important',
        '&:hover': {
          backgroundColor: `${CALLOUT_BACKGROUND} !important`,
        },
      },
      '&.Mui-selected + &': {
        borderLeft: `1px solid ${OUTLINED_INPUT_BORDER} !important`,
      },
      '&:hover': {
        backgroundColor: `${theme.palette.common.white} !important`,
      },
    },
    todayButton: {
      height: 40,
      width: 'auto !important',
      minWidth: 'unset !important',
      maxWidth: 'none',
      flex: '0 0 auto',
      borderRadius: 8,
      textTransform: 'none',
      flexShrink: 0,
      boxShadow: 'none',
      padding: '8px 12px !important',
      borderColor: OUTLINED_INPUT_BORDER,
      color: CALLOUT_FOREGROUND,
      backgroundColor: theme.palette.common.white,
      '& .MuiButton-label, & .MuiToggleButton-label': {
        textTransform: 'none',
        padding: 0,
        ...toolbarControlText,
      },
      '&:hover': {
        boxShadow: 'none',
        backgroundColor: '#F4F6F8',
        borderColor: OUTLINED_INPUT_BORDER,
      },
    },
    todayButtonSelected: {
      backgroundColor: CALLOUT_BACKGROUND,
      color: CALLOUT_FOREGROUND,
      borderColor: OUTLINED_INPUT_BORDER,
      '&:hover': {
        backgroundColor: CALLOUT_BACKGROUND,
        borderColor: OUTLINED_INPUT_BORDER,
      },
    },
    datePickerButton: {
      height: 40,
      borderRadius: 8,
      textTransform: 'none',
      flexShrink: 0,
      borderColor: OUTLINED_INPUT_BORDER,
      color: CALLOUT_FOREGROUND,
      backgroundColor: theme.palette.common.white,
      padding: '8px 12px',
      '& .MuiButton-label': {
        textTransform: 'none',
        ...toolbarControlText,
      },
      '&:hover': {
        backgroundColor: '#F4F6F8',
        borderColor: OUTLINED_INPUT_BORDER,
      },
    },
    datePickerPopover: {
      marginTop: theme.spacing(0.5),
      borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
      border: `1px solid ${OUTLINED_INPUT_BORDER}`,
      backgroundColor: theme.palette.common.white,
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.12)',
      overflow: 'hidden',
    },
    dateCalendar: {
      '& .MuiPickersArrowSwitcher-button': {
        color: CALLOUT_FOREGROUND,
      },
      '& .MuiPickersCalendarHeader-switchViewButton': {
        color: CALLOUT_FOREGROUND,
      },
      '& .MuiPickersCalendarHeader-switchViewIcon': {
        willChange: 'transform',
        transition: theme.transitions.create('transform'),
      },
    },
    toolbarFilterGroup: {
      display: 'flex',
      alignItems: 'stretch',
      flex: '0 0 auto',
      borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
      border: `1px solid ${OUTLINED_INPUT_BORDER}`,
      backgroundColor: theme.palette.common.white,
      overflow: 'hidden',
      [compactToolbar]: {
        flex: '0 1 auto',
        minWidth: 0,
        maxWidth: '100%',
      },
    },
    toolbarFilterDivider: {
      width: 1,
      alignSelf: 'stretch',
      backgroundColor: OUTLINED_INPUT_BORDER,
      flexShrink: 0,
    },
    bookingsOnlyFilter: {
      borderRadius: 0,
      border: 'none !important',
      padding: '8px 12px !important',
      display: 'inline-flex',
      alignItems: 'center',
      '& .MuiButton-label': {
        whiteSpace: 'nowrap',
        ...toolbarControlText,
      },
      '& .MuiButton-startIcon': {
        marginRight: theme.spacing(0.75),
        marginLeft: 0,
        color: CALLOUT_FOREGROUND,
      },
      '&:hover': {
        border: 'none !important',
      },
    },
    locationWrapper: {
      flex: '0 0 auto',
      width: 'auto',
      display: 'flex',
      alignItems: 'center',
      [compactToolbar]: {
        flex: '0 1 auto',
        minWidth: 0,
        maxWidth: '100%',
      },
    },
    locationWrapperGrouped: {
      flex: '0 1 auto',
      minWidth: 0,
    },
    locationFormControl: {
      width: 'auto',
      margin: 0,
      [compactToolbar]: {
        width: 'auto',
        maxWidth: '100%',
      },
    },
    locationOutlinedInput: {
      height: 40,
      minHeight: 40,
      borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        backgroundColor: '#F4F6F8',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: OUTLINED_INPUT_BORDER,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: OUTLINED_INPUT_FOCUS_BORDER,
      },
    },
    locationOutlinedInputGrouped: {
      height: 40,
      minHeight: 40,
      borderRadius: 0,
      backgroundColor: theme.palette.common.white,
      '& fieldset': {
        border: 'none',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&:hover': {
        backgroundColor: '#F4F6F8',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    locationNotchedOutline: {
      borderColor: OUTLINED_INPUT_BORDER,
    },
    locationNotchedOutlineGrouped: {
      border: 'none',
    },
    locationSelectInput: {
      padding: '8px 32px 8px 12px !important',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      ...toolbarControlText,
    },
    locationSelectIcon: {
      color: CALLOUT_FOREGROUND,
      right: 8,
      top: 'calc(50% - 12px)',
    },
    locationMenuPaper: getOutlinedSelectMenuPaperStyles(theme),
    calendarCard: {
      border: '1px solid #E0E0E0',
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: theme.palette.common.white,
      boxShadow: 'none',
    },
    calendarHeader: {
      padding: theme.spacing(2, 3),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: '#F1F1F1',
    },
    calendarTitle: {
      fontWeight: 500,
      fontSize: '1rem',
      color: CALLOUT_FOREGROUND,
    },
    weekHeaderRow: {
      display: 'flex',
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: '#F1F1F1',
      width: '100%',
    },
    weekHeaderSpacer: {
      flexShrink: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    weekHeaderCell: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(1.5, 0.5),
      borderLeft: `1px solid ${theme.palette.divider}`,
      [compactToolbar]: {
        padding: theme.spacing(1, 0.25),
      },
      '&:first-of-type': {
        borderLeft: 'none',
      },
    },
    weekHeaderLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      fontSize: '0.8125rem',
      lineHeight: 1.4,
      color: theme.palette.text.secondary,
      fontWeight: 500,
      minWidth: 0,
      [compactToolbar]: {
        flexDirection: 'column',
        gap: 2,
        fontSize: '0.75rem',
      },
    },
    weekDayName: {
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      [compactToolbar]: {
        fontSize: '0.6875rem',
        lineHeight: 1.2,
        fontWeight: 500,
      },
    },
    weekDayNumber: {
      fontWeight: 600,
      color: CALLOUT_FOREGROUND,
      lineHeight: 1.2,
      [compactToolbar]: {
        fontSize: '0.8125rem',
      },
    },
    weekDayNumberToday: {
      display: 'inline-block',
      flexShrink: 0,
      width: 28,
      height: 28,
      borderRadius: '50%',
      backgroundColor: OUTLINED_INPUT_FOCUS_BORDER,
      color: theme.palette.common.white,
      fontWeight: 700,
      fontSize: '0.8125rem',
      lineHeight: '28px',
      textAlign: 'center',
      padding: 0,
      boxSizing: 'border-box',
      fontFeatureSettings: '"tnum"',
      [compactToolbar]: {
        width: 24,
        height: 24,
        fontSize: '0.75rem',
        lineHeight: '24px',
      },
    },
    timeGridBody: {
      display: 'flex',
      paddingTop: theme.spacing(1.5),
      backgroundColor: theme.palette.common.white,
      width: '100%',
      minWidth: 0,
      maxHeight: 'min(70vh, 900px)',
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    hourLabelsColumn: {
      flexShrink: 0,
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      boxSizing: 'border-box',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        pointerEvents: 'none',
      },
    },
    hourLabel: {
      boxSizing: 'border-box',
      paddingRight: theme.spacing(1.25),
      paddingTop: 6,
      fontSize: 11,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      textAlign: 'right',
      transform: 'translateY(-8px)',
      '&:first-of-type': {
        transform: 'none',
        paddingTop: 10,
      },
      [theme.breakpoints.down('xs')]: {
        paddingRight: theme.spacing(0.75),
        fontSize: 10,
      },
    },
    dayColumns: {
      display: 'flex',
      flex: '1 1 auto',
      minWidth: 0,
      boxSizing: 'border-box',
    },
    dayColumn: {
      position: 'relative',
      flex: '1 1 0',
      minWidth: 0,
      borderLeft: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.common.white,
      overflow: 'hidden',
      '&:first-of-type': {
        borderLeft: 'none',
      },
      '& $hourCell:last-child': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    hourCell: {
      display: 'block',
      boxSizing: 'border-box',
      borderTop: `1px solid ${theme.palette.divider}`,
      position: 'relative',
      flexShrink: 0,
      backgroundColor: theme.palette.common.white,
      zIndex: 1,
    },
    hourCellOutsideBookable: {
      backgroundColor: SCHEDULE_AVAILABILITY_OUTSIDE_HOURS,
    },
    hourCellBookable: {
      '&:hover': {
        zIndex: 5,
        backgroundColor: 'transparent',
      },
      '&:hover $bookSlotTarget': {
        backgroundColor: '#E8EFFF',
        borderColor: OUTLINED_INPUT_FOCUS_BORDER,
      },
      '&:hover $bookSlotIcon': {
        opacity: 1,
      },
    },
    bookSlotTarget: {
      position: 'absolute',
      top: 4,
      left: 4,
      right: 4,
      bottom: 4,
      borderRadius: 10,
      border: '1px dashed transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      margin: 0,
      appearance: 'none',
      background: 'transparent',
      textDecoration: 'none',
      color: 'inherit',
      cursor: 'pointer',
      zIndex: 1,
      transition: 'background-color 120ms ease, border-color 120ms ease',
      font: 'inherit',
    },
    bookSlotIcon: {
      width: 20,
      height: 20,
      fontSize: 20,
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    eventBlock: {
      position: 'absolute',
      left: 4,
      right: 4,
      borderRadius: 8,
      padding: '6px 8px',
      overflow: 'hidden',
      cursor: 'default',
      border: 'none',
      zIndex: 3,
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      boxShadow: 'none',
      [compactToolbar]: {
        left: 2,
        right: 2,
        padding: '4px 4px',
        borderRadius: 6,
      },
    },
    eventBlockInteractive: {
      cursor: 'pointer',
      '&:hover': {
        filter: 'brightness(0.97)',
      },
      '&:focus-visible': {
        outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
        outlineOffset: 1,
      },
    },
    eventBlockBlocked: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 4,
      overflow: 'visible',
      pointerEvents: 'none',
    },
    eventBlockIcon: {
      fontSize: 14,
      marginTop: 2,
      flexShrink: 0,
      width: 14,
      height: 14,
    },
    eventBlockText: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
      flex: 1,
    },
    eventBlockedTitle: {
      display: 'block',
      fontWeight: 600,
      fontSize: '0.8125rem',
      lineHeight: 1.35,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      [compactToolbar]: {
        fontSize: '0.6875rem',
      },
    },
    eventBlockedSubtitle: {
      display: 'block',
      fontSize: '0.75rem',
      lineHeight: 1.35,
      fontWeight: 400,
      opacity: 0.88,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      [compactToolbar]: {
        fontSize: '0.625rem',
      },
    },
    eventTitle: {
      display: 'block',
      fontWeight: 600,
      fontSize: '0.8125rem',
      lineHeight: 1.35,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [compactToolbar]: {
        fontSize: '0.6875rem',
      },
    },
    eventSubtitle: {
      display: 'block',
      fontSize: '0.75rem',
      lineHeight: 1.35,
      fontWeight: 400,
      opacity: 0.88,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [compactToolbar]: {
        fontSize: '0.625rem',
      },
    },
    monthGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
      backgroundColor: '#FAFBFC',
    },
    monthWeekdayHeader: {
      padding: theme.spacing(1.25),
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: '#F1F1F1',
    },
    monthDayCell: {
      minHeight: 120,
      minWidth: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.75),
      backgroundColor: theme.palette.common.white,
      '&:nth-child(7n)': {
        borderRight: 'none',
      },
    },
    monthDayCellBookable: {
      '&:hover': {
        backgroundColor: '#F0F4F8',
      },
      '&:hover $monthBookSlotTarget': {
        backgroundColor: '#E8EFFF',
        borderColor: OUTLINED_INPUT_FOCUS_BORDER,
      },
      '&:hover $monthBookSlotIcon': {
        opacity: 1,
      },
    },
    monthDayOutside: {
      backgroundColor: '#FAFBFC',
      '& $monthDayNumber': {
        color: theme.palette.text.disabled,
      },
    },
    monthDayToday: {
      '& $monthDayNumber': {
        backgroundColor: OUTLINED_INPUT_FOCUS_BORDER,
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: '#0046D9',
        },
      },
    },
    monthDayNumber: {
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: '0.8125rem',
      marginBottom: theme.spacing(1),
      color: CALLOUT_FOREGROUND,
      '&:is(button)': {
        border: 'none',
        padding: 0,
        appearance: 'none',
        background: 'transparent',
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: '#F0F4F8',
        },
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
          outlineOffset: 1,
        },
      },
    },
    monthEventPill: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      border: 'none',
      borderRadius: 8,
      padding: '5px 8px',
      marginBottom: 4,
      textAlign: 'left',
      cursor: 'default',
      fontSize: '0.75rem',
      lineHeight: 1.35,
      fontWeight: 500,
      overflow: 'hidden',
    },
    monthEventPillInteractive: {
      cursor: 'pointer',
      '&:hover': {
        filter: 'brightness(0.97)',
      },
      '&:focus-visible': {
        outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
        outlineOffset: 1,
      },
    },
    monthEventPillBlocked: {
      cursor: 'default',
      pointerEvents: 'none',
    },
    monthEventTime: {
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    monthEventTitle: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    monthMoreButton: {
      display: 'block',
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      border: 'none',
      padding: 0,
      appearance: 'none',
      background: 'transparent',
      textAlign: 'left',
      '&:hover': {
        color: CALLOUT_FOREGROUND,
      },
      '&:focus': {
        outline: 'none',
      },
      '&:focus-visible': {
        outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
        outlineOffset: 1,
      },
    },
    monthBookSlotTarget: {
      marginTop: 'auto',
      minHeight: 28,
      borderRadius: 8,
      border: '1px dashed transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      margin: 0,
      appearance: 'none',
      background: 'transparent',
      textDecoration: 'none',
      color: 'inherit',
      cursor: 'pointer',
      transition: 'background-color 120ms ease, border-color 120ms ease',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-visible': {
        outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
        outlineOffset: 1,
      },
    },
    monthBookSlotTargetLink: {
      cursor: 'pointer',
    },
    monthBookSlotIcon: {
      width: 20,
      height: 20,
      fontSize: 20,
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    listCard: {
      border: '1px solid #E0E0E0',
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: theme.palette.common.white,
    },
    listSection: {
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:first-of-type': {
        borderTop: 'none',
      },
    },
    listSectionHeader: {
      padding: theme.spacing(1.5, 3),
      backgroundColor: '#F1F1F1',
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontWeight: 500,
      fontSize: '0.875rem',
      color: CALLOUT_FOREGROUND,
    },
    listRows: {
      borderTop: 'none',
    },
    listSectionEmpty: {
      padding: theme.spacing(2, 3, 2.5),
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      backgroundColor: theme.palette.common.white,
      boxShadow: 'none',
    },
    mobileAgendaCalendar: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      justifyContent: 'stretch',
      padding: theme.spacing(0, 1, 0.5),
      boxShadow: 'none',
    },
    mobileDateCalendar: {
      width: '100%',
      maxWidth: 'none',
      margin: 0,
      boxShadow: 'none',
      '& .MuiDateCalendar-root': {
        boxShadow: 'none',
      },
      '& .MuiPickersCalendarHeader-root': {
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        minHeight: 28,
        maxHeight: 32,
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
      },
      '& .MuiPickersCalendarHeader-label': {
        fontSize: '0.9375rem',
      },
      '& .MuiPickersArrowSwitcher-button': {
        padding: 4,
      },
      '& .MuiDayCalendar-header': {
        justifyContent: 'space-between',
      },
      '& .MuiDayCalendar-weekDayLabel': {
        flex: 1,
        width: 'auto',
        height: 28,
        margin: 0,
        fontSize: '0.75rem',
      },
      '& .MuiDayCalendar-weekContainer': {
        justifyContent: 'space-between',
        margin: '1px 0',
        minHeight: 44,
      },
      '& .MuiDayCalendar-slideTransition': {
        minHeight: 'unset !important',
      },
      '& .MuiPickersSlideTransition-root': {
        minHeight: 'unset !important',
      },
      '& .MuiPickersDay-root': {
        width: 32,
        height: 32,
        fontSize: '0.8125rem',
        margin: 0,
        boxShadow: 'none',
      },
      '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: '#0053FF',
        boxShadow: 'none',
      },
    },
    agendaDayRoot: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: 40,
      minHeight: 40,
      paddingBottom: 2,
      boxSizing: 'border-box',
    },
    agendaPickersDay: {
      margin: 0,
      flexShrink: 0,
    },
    agendaDayDots: {
      display: 'flex',
      gap: 2,
      justifyContent: 'center',
      marginTop: 2,
      minHeight: 4,
      flexShrink: 0,
      pointerEvents: 'none',
    },
    agendaDayDot: {
      width: 4,
      height: 4,
      borderRadius: '50%',
      flexShrink: 0,
    },
  }
})

export default useCalendarStyles
